export interface SeatClassOpts {
  name: string;
  seats: number;
  emissionsPerSeat: number;
}

const defaultSeatClassOpts: SeatClassOpts = {
  name: "",
  seats: 0,
  emissionsPerSeat: 0,
};

export interface SearchResultsOpts {
  emissionsPerPassenger: number;
  emissionsTotal: number;
  aircraftTypeName: string;
  distance: number;
  aircraftRegistration: string;
  flightsOver12Months: number;
  seatClasses: SeatClassOpts[];
}

const defaultSearchResultsOpts: SearchResultsOpts = {
  emissionsPerPassenger: 0,
  emissionsTotal: 0,
  aircraftTypeName: "",
  distance: 0,
  aircraftRegistration: "",
  flightsOver12Months: 0,
  seatClasses: [],
};

export class SeatClass {
  private readonly _name: string;
  private readonly _seats: number;
  private readonly _emissionsPerSeat: number;

  constructor(opts: Partial<SeatClassOpts> = {}) {
    const options = { ...defaultSeatClassOpts, ...opts };
    this._name = options.name;
    this._seats = options.seats;
    this._emissionsPerSeat = options.emissionsPerSeat;
  }

  public toOpts(): SeatClassOpts {
    return {
      name: this._name,
      seats: this._seats,
      emissionsPerSeat: this._emissionsPerSeat,
    };
  }

  public get name(): string {
    return this._name;
  }

  public get seats(): number {
    return this._seats;
  }

  public get emissionsPerSeat(): number {
    return this._emissionsPerSeat;
  }
}

export class SearchResults {
  private readonly _emissionsPerPassenger: number;
  private readonly _emissionsTotal: number;
  private readonly _aircraftTypeName: string;
  private readonly _distance: number;
  private readonly _aircraftRegistration: string;
  private readonly _flightsOver12Months: number;
  private readonly _seatClasses: SeatClass[];

  constructor(opts: Partial<SearchResultsOpts> = {}) {
    const options = { ...defaultSearchResultsOpts, ...opts };
    this._emissionsPerPassenger = options.emissionsPerPassenger;
    this._emissionsTotal = options.emissionsTotal;
    this._aircraftTypeName = options.aircraftTypeName;
    this._distance = options.distance;
    this._aircraftRegistration = options.aircraftRegistration;
    this._flightsOver12Months = options.flightsOver12Months;
    this._seatClasses = (options.seatClasses || []).map(
      (seatClass) => new SeatClass(seatClass),
    );
  }

  public toOpts(): SearchResultsOpts {
    return {
      emissionsPerPassenger: this._emissionsPerPassenger,
      emissionsTotal: this._emissionsTotal,
      aircraftTypeName: this._aircraftTypeName,
      distance: this._distance,
      aircraftRegistration: this._aircraftRegistration,
      flightsOver12Months: this._flightsOver12Months,
      seatClasses: this._seatClasses.map((seatClass) => seatClass.toOpts()),
    };
  }

  public get emissionsPerPassenger(): number {
    return this._emissionsPerPassenger;
  }

  public get emissionsTotal(): number {
    return this._emissionsTotal;
  }

  public get aircraftTypeName(): string {
    return this._aircraftTypeName;
  }

  public get distance(): number {
    return this._distance;
  }

  public get aircraftRegistration(): string {
    return this._aircraftRegistration;
  }

  public get flightsOver12Months(): number {
    return this._flightsOver12Months;
  }

  public get seatClasses(): SeatClass[] {
    return [...this._seatClasses];
  }
}
